import { useState, useEffect, useCallback } from 'react';
import { useQuery } from 'react-query';
import { usePaginationParams } from 'utils/hooks/usePaginationParams';
import MaterialTableNew, { MaterialTableNewProps } from '../MaterialTableNew/MaterialTableNew';

type PaginatedTableWithQueryLocaledProps<T extends { locale: string }> = {
  queryKey: unknown[];
  queryFn(params: any): Promise<{ data: T[]; total: number }>;
  localesOptions: string[];
} & Omit<
  MaterialTableNewProps<T>,
  | 'data'
  | 'loading'
  | 'rowCount'
  | 'onPageSizeChange'
  | 'onPageChange'
  | 'order'
  | 'orderBy'
  | 'filters'
  | 'onRequestSort'
  | 'onFilterChange'
>;

const PaginatedTableWithQueryLocaled = <T extends { locale: string }>({
  queryFn,
  queryKey,
  localesOptions,
  ...tableProps
}: PaginatedTableWithQueryLocaledProps<T>) => {
  const {
    apiParams,
    page,
    setPage,
    limit,
    setLimit,
    order,
    orderBy,
    filters,
    setFilters,
    toggleOrderBy,
  } = usePaginationParams<T>(tableProps.type);

  const [, setSelectedLocale] = useState(localesOptions[0]);
  
  useEffect(() => {
    if (localesOptions.length > 0) {
      const defaultLocale = localesOptions[0];
      if (!filters.locale?.value) {
        setFilters(prevFilters => ({
          ...prevFilters,
          locale: { value: defaultLocale }
        }));
        setSelectedLocale(defaultLocale);
      }
    }
  }, [localesOptions, filters.locale?.value, setFilters]);

  const { isLoading, data: result, isFetching } = useQuery(
    [...queryKey, apiParams],
    () => queryFn(apiParams),
    { keepPreviousData: true, refetchOnMount: 'always' }
  );

  const handleFilterChange = useCallback((nextFilters) => {
    setPage(0);
    setFilters(nextFilters);
  }, [setFilters, setPage]);

  useEffect(() => {
    if (!result || !limit) return;

    const maxPage = Math.max(0, Math.ceil(result.total / limit) - 1);
    if (page > maxPage) setPage(maxPage);
  }, [result, limit, page, setPage]);

  //const filteredData = (result?.data || []).filter(item => localesOptions.includes(item.locale));

  return (
    <MaterialTableNew
      //data={filteredData || []}
      data={result?.data || []}
      loading={isLoading || isFetching}
      //rowCount={filteredData.length || 0}
      rowCount={result?.total || 0}
      page={page}
      rowsPerPage={limit}
      onPageSizeChange={setLimit}
      onPageChange={setPage}
      order={order}
      orderBy={orderBy}
      filters={filters}
      onRequestSort={toggleOrderBy}
      onFilterChange={handleFilterChange}
      {...tableProps}
    />
  );
};

export default PaginatedTableWithQueryLocaled;
